import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { api } from '../lib/api/client'

export interface User {
  id: string
  email: string
  full_name: string
  phone?: string
  avatar_url?: string
  is_active: boolean
  is_superuser: boolean
  is_verified: boolean
  locale: string
  timezone: string
  roles: Role[]
  tenant_id: string
  last_login?: string
}

export interface Role {
  id: string
  name: string
  display_name?: string
  description?: string
  permissions: (Permission | string)[]
  permissions_count?: number
}

export interface Permission {
  id?: string
  name?: string
  resource: string
  action: string
  description?: string
}

export interface SystemPermissionGroup {
  id: string
  title: string
  iconName: string
  permissions: {
    key: string
    title: string
    description: string
  }[]
}

export const SYSTEM_PERMISSIONS: SystemPermissionGroup[] = [
  {
    id: 'dashboard',
    title: 'داشبورد و آمار عمومی',
    iconName: 'LayoutDashboard',
    permissions: [
      { key: 'dashboard.read', title: 'مشاهده داشبورد', description: 'دسترسی به نمودارها، فروش روزانه و خلاصه آمار سیستم' }
    ]
  },
  {
    id: 'inventory',
    title: 'مدیریت محصولات و انبار',
    iconName: 'Package',
    permissions: [
      { key: 'product.read', title: 'مشاهده لیست کالاها', description: 'دیدن محصولات، دسته‌بندی‌ها، برندها و واحدهای سنجش' },
      { key: 'product.write', title: 'افزودن و ویرایش کالا', description: 'تعریف محصول جدید، تغییر قیمت و مشخصات فنی' },
      { key: 'stock.read', title: 'مشاهده موجودی انبارها', description: 'گزارش موجودی کالاها، انبارها و کسری موجودی' },
      { key: 'stock.receive', title: 'ثبت ورود کالا (خرید)', description: 'ثبت رسید ورود جنس به انبار از تامین‌کنندگان' },
      { key: 'stock.transfer', title: 'انتقال بین انبارها', description: 'انتقال کالا از یک انبار به انبار دیگر' },
      { key: 'stock.adjust', title: 'اصلاح موجودی کالا', description: 'تنظیم دستی و تعدیل موجودی در انبار' },
      { key: 'stock.count', title: 'انبارگردانی و شمارش', description: 'ثبت شمارش دوره‌ای و مغایرت‌گیری انبار' }
    ]
  },
  {
    id: 'pos',
    title: 'صندوق و فروش نقدی (POS)',
    iconName: 'ShoppingCart',
    permissions: [
      { key: 'pos.read', title: 'مشاهده منوی صندوق', description: 'دسترسی به صفحه اصلی صندوق فروش' },
      { key: 'pos.sell', title: 'صدور فاکتور و فروش', description: 'ثبت کالا در سبد خرید، اعمال تخفیف و تسویه حساب' },
      { key: 'pos.receipts', title: 'مشاهده و چاپ رسیدها', description: 'دسترسی به سوابق رسیدهای صادره و چاپ مجدد' },
      { key: 'pos.shift', title: 'مدیریت شیفت‌های صندوق', description: 'شروع شیفت جدید، ثبت موجودی اولیه و بستن شیفت' },
      { key: 'pos.payment', title: 'ثبت دریافت‌ها و تراکنش‌ها', description: 'مشاهده و ثبت تراکنش‌های کارت‌خوان و نقدی' }
    ]
  },
  {
    id: 'purchase',
    title: 'خرید و تامین‌کنندگان',
    iconName: 'Truck',
    permissions: [
      { key: 'purchase.read', title: 'سفارشات خرید', description: 'مشاهده و ثبت پیش‌فاکتورها و سفارشات خرید' },
      { key: 'supplier.read', title: 'مدیریت تامین‌کنندگان', description: 'مشاهده اطلاعات تماس و سوابق شرکت‌های تامین‌کننده' },
      { key: 'bill.read', title: 'فاکتورهای خرید (Bills)', description: 'مشاهده صورت‌حساب‌های بدهی به تامین‌کنندگان' }
    ]
  },
  {
    id: 'sales',
    title: 'سوابق و گزارشات فروش',
    iconName: 'TrendingUp',
    permissions: [
      { key: 'sales.read', title: 'مشاهده سوابق فروش', description: 'لیست کلیه فاکتورها و فروش‌های گذشته' },
      { key: 'sales.report', title: 'گزارشات تحلیلی فروش', description: 'گزارش سود و زیان، کالاهای پرفروش و آمار فروش' }
    ]
  },
  {
    id: 'customer',
    title: 'مدیریت مشتریان',
    iconName: 'Users',
    permissions: [
      { key: 'customer.read', title: 'مشاهده و ثبت مشتریان', description: 'لیست مشتریان، شماره تماس و سوابق خرید' }
    ]
  },
  {
    id: 'admin',
    title: 'مدیریت سیستم و دسترسی‌ها',
    iconName: 'Shield',
    permissions: [
      { key: 'admin.users', title: 'مدیریت کاربران پنل', description: 'افزودن کاربر جدید، تخصیص نقش و تغییر رمز' },
      { key: 'admin.roles', title: 'مدیریت نقش‌ها و مجوزها', description: 'تعریف نقش جدید، تنظیم توضیحات و چک‌باکس‌های دسترسی' },
      { key: 'settings.manage', title: 'تنظیمات عمومی فروشگاه', description: 'تغییر نام فروشگاه، تنظیمات مالیات و انبار' }
    ]
  }
]

export interface Tenant {
  id: string
  name: string
  slug: string
  domain?: string
  logo_url?: string
  primary_color: string
  is_active: boolean
  settings: Record<string, any>
}

interface AuthState {
  user: User | null
  tenant: Tenant | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  activeRoleName: string

  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  refreshAccessToken: () => Promise<boolean>
  initializeAuth: () => Promise<void>
  updateUser: (data: Partial<User>) => Promise<void>
  changePassword: (current: string, newPassword: string) => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (token: string, password: string) => Promise<void>
  switchActiveRole: (roleName: string, customPermissions?: string[]) => void
  clearError: () => void
}

export interface RegisterData {
  email: string
  password: string
  full_name: string
  phone?: string
  tenant_slug?: string
  tenant_name?: string
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: {
        id: 'usr_1',
        email: 'admin@kaseb.app',
        full_name: 'مدیر فروشگاه',
        is_active: true,
        is_superuser: true,
        is_verified: true,
        locale: 'fa',
        timezone: 'Asia/Tehran',
        tenant_id: 'ten_1',
        roles: [
          {
            id: 'role_1',
            name: 'مدیر سیستم',
            display_name: 'مدیر سیستم',
            description: 'دسترسی کامل و بدون محدودیت به تمامی بخش‌های نرم‌افزار',
            permissions: SYSTEM_PERMISSIONS.flatMap(g => g.permissions.map(p => p.key))
          }
        ]
      },
      tenant: {
        id: 'ten_1',
        name: 'فروشگاه کسب',
        slug: 'kaseb',
        primary_color: '#3b82f6',
        is_active: true,
        settings: {}
      },
      accessToken: 'demo_token',
      refreshToken: 'demo_refresh_token',
      isAuthenticated: true,
      isLoading: false,
      error: null,
      activeRoleName: 'مدیر سیستم',

      clearError: () => set({ error: null }),

      switchActiveRole: (roleName: string, customPermissions?: string[]) => {
        const state = get()
        let perms: string[] = []

        if (customPermissions && customPermissions.length > 0) {
          perms = customPermissions
        } else if (roleName === 'مدیر سیستم' || roleName === 'Admin') {
          perms = SYSTEM_PERMISSIONS.flatMap(g => g.permissions.map(p => p.key))
        } else if (roleName === 'صندوق‌دار') {
          perms = ['dashboard.read', 'pos.read', 'pos.sell', 'pos.receipts', 'pos.shift', 'pos.payment', 'customer.read']
        } else if (roleName === 'انباردار') {
          perms = ['dashboard.read', 'inventory.read', 'product.read', 'product.write', 'stock.read', 'stock.receive', 'stock.transfer', 'stock.adjust', 'stock.count']
        } else if (roleName === 'حسابدار') {
          perms = ['dashboard.read', 'sales.read', 'sales.report', 'purchase.read', 'supplier.read', 'bill.read', 'customer.read', 'pos.payment']
        } else {
          perms = ['dashboard.read', 'pos.read', 'pos.sell']
        }

        const isSuper = roleName === 'مدیر سیستم' || roleName === 'Admin'
        const updatedUser: User = state.user ? {
          ...state.user,
          is_superuser: isSuper,
          roles: [
            {
              id: 'role_' + roleName,
              name: roleName,
              display_name: roleName,
              description: `دسترسی‌های فعال شده برای نقش ${roleName}`,
              permissions: perms
            }
          ]
        } : {
          id: 'usr_demo',
          email: 'user@kaseb.app',
          full_name: 'کاربر سیستم',
          is_active: true,
          is_superuser: isSuper,
          is_verified: true,
          locale: 'fa',
          timezone: 'Asia/Tehran',
          tenant_id: 'ten_1',
          roles: [
            {
              id: 'role_' + roleName,
              name: roleName,
              display_name: roleName,
              permissions: perms
            }
          ]
        }

        set({ user: updatedUser, activeRoleName: roleName })
      },

      login: async (email: string, password: string, rememberMe = false) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/login', {
            email,
            password,
            remember_me: rememberMe,
          })

          const { access_token, refresh_token, user, tenant } = response.data

          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

          set({
            user,
            tenant,
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
          })

          if (rememberMe) {
            localStorage.setItem('refresh_token', refresh_token)
          }
        } catch (error: any) {
          const message = error.response?.data?.detail || 'ورود ناموفق بود'
          set({ error: message, isLoading: false, isAuthenticated: false })
          throw error
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/auth/register', data)
          const { access_token, refresh_token, user, tenant } = response.data

          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

          set({
            user,
            tenant,
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
          })
          localStorage.setItem('refresh_token', refresh_token)
        } catch (error: any) {
          const message = error.response?.data?.detail || 'ثبت‌نام ناموفق بود'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      logout: async () => {
        const { refreshToken } = get()
        try {
          if (refreshToken) {
            await api.post('/auth/logout', { refresh_token: refreshToken })
          }
        } catch {
          // Ignore logout errors
        } finally {
          delete api.defaults.headers.common['Authorization']
          localStorage.removeItem('refresh_token')
          set({
            user: null,
            tenant: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) return false

        try {
          const response = await api.post('/auth/refresh', { refresh_token: refreshToken })
          const { access_token, refresh_token: newRefreshToken } = response.data

          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

          set({
            accessToken: access_token,
            refreshToken: newRefreshToken,
          })

          localStorage.setItem('refresh_token', newRefreshToken)
          return true
        } catch {
          get().logout()
          return false
        }
      },

      initializeAuth: async () => {
        const state = get()
        if (state.isAuthenticated && state.user) {
          set({ isLoading: false })
          return
        }
        const storedRefreshToken = localStorage.getItem('refresh_token')
        if (!storedRefreshToken) {
          set({ isLoading: false })
          return
        }

        set({ refreshToken: storedRefreshToken, isLoading: true })

        const success = await get().refreshAccessToken()
        if (!success) {
          set({ isLoading: false })
        }
      },

      updateUser: async (data: Partial<User>) => {
        set({ isLoading: true })
        try {
          const response = await api.patch('/auth/me', data)
          set({ user: response.data, isLoading: false })
        } catch (error: any) {
          set({ error: error.response?.data?.detail || 'به‌روزرسانی ناموفق بود', isLoading: false })
          throw error
        }
      },

      changePassword: async (current: string, newPassword: string) => {
        set({ isLoading: true })
        try {
          await api.post('/auth/change-password', {
            current_password: current,
            new_password: newPassword,
          })
          set({ isLoading: false })
        } catch (error: any) {
          const message = error.response?.data?.detail || 'تغییر رمز عبور ناموفق بود'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      forgotPassword: async (email: string) => {
        try {
          await api.post('/auth/forgot-password', { email })
        } catch (error: any) {
          throw error
        }
      },

      resetPassword: async (token: string, password: string) => {
        try {
          await api.post('/auth/reset-password', { token, new_password: password })
        } catch (error: any) {
          throw error
        }
      },
    }),
    {
      name: 'kaseb-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        activeRoleName: state.activeRoleName,
      }),
    }
  )
)

// Helper to check permissions
export function usePermissions() {
  const { user } = useAuthStore()

  const hasPermission = (permissionKey: string, action?: string) => {
    if (!user) return false
    if (user.is_superuser) return true
    if (!user.roles || !Array.isArray(user.roles)) return true

    // Admin role has all permissions
    const isAdmin = user.roles.some(r => r.name === 'Admin' || r.name === 'مدیر سیستم')
    if (isAdmin) return true

    const fullKey = action ? `${permissionKey}.${action}` : permissionKey

    return user.roles.some(role => {
      if (!role.permissions) return false
      return role.permissions.some(p => {
        if (typeof p === 'string') {
          return p === fullKey || p === permissionKey || p.startsWith(permissionKey + '.')
        }
        if (p.resource && p.action) {
          const pKey = `${p.resource}.${p.action}`
          return pKey === fullKey || p.resource === permissionKey
        }
        return p.name === fullKey || p.name === permissionKey
      })
    })
  }

  const hasRole = (roleName: string) => {
    if (!user) return false
    if (!user.roles || !Array.isArray(user.roles)) return false
    return user.roles.some(r => r.name === roleName)
  }

  const hasAnyRole = (roleNames: string[]) => {
    if (!user) return false
    if (!user.roles || !Array.isArray(user.roles)) return false
    return user.roles.some(r => roleNames.includes(r.name))
  }

  return { hasPermission, hasRole, hasAnyRole }
}

// Selectors
export const useUser = () => useAuthStore(state => state.user)
export const useTenant = () => useAuthStore(state => state.tenant)
export const useIsAuthenticated = () => useAuthStore(state => state.isAuthenticated)
export const useAuthLoading = () => useAuthStore(state => state.isLoading)
export const useAuthError = () => useAuthStore(state => state.error)